import React, { useEffect, useState } from "react";
import api from "../api/axiosConfig";
import { useAuth } from '../context/AuthContext';
import ConfirmModal from "./ConfirmModal";

const StoreMaintenance = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState({ name: "", location: "" });
  const [submitted, setSubmitted] = useState(false);
  const { isAuthenticated, getAccessToken } = useAuth();

  useEffect(() => {
    const fetchStores = async () => {
      if (!isAuthenticated || !getAccessToken()) return;

      try {
        const res = await api.get('/store');
        
        // Ensure stores is always an array
        const storesData = Array.isArray(res.data) ? res.data : 
                          (res.data.stores || res.data.data || []);
        setStores(storesData);
      } catch (err) {
        setError("Error fetching stores");
        console.error("Fetch stores error:", err);
        // Ensure stores is always an array even on error
        setStores([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, [isAuthenticated]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Open Add Store Modal
  const openAddModal = () => {
    setSelectedStore(null);
    setFormData({ name: "", location: "" });
    setSubmitted(false);
    setModalOpen(true);
  };

  // Open Edit Store Modal
  const openEditModal = (store) => {
    setSelectedStore(store);
    setFormData({ name: store.name, location: store.location });
    setSubmitted(false);
    setModalOpen(true);
  };

  // Close Modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedStore(null);
    setSubmitted(false);
  };

  // Open Delete Confirmation Modal
  const openConfirmModal = (store) => {
    setSelectedStore(store);
    setConfirmModalOpen(true);
  };

  // Delete Store
  const deleteStore = async () => {
    if (!selectedStore) return;

    try {
      await api.delete(`/store/${selectedStore._id}`);
      setStores(stores.filter((store) => store._id !== selectedStore._id));
    } catch (err) {
      setError("Error deleting store");
      console.error("Delete store error:", err);
    } finally {
      setConfirmModalOpen(false);
      setSelectedStore(null);
    }
  };

  // Handle Create or Update Store
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // Validate required fields
    if (!formData.name || !formData.location) {
      return;
    }

    try {
      if (selectedStore) {
        await api.put(`/store/${selectedStore._id}`, formData);
      } else {
        await api.post('/store', formData);
      }

      // Refresh stores after successful operation
      const res = await api.get('/store');
      
      const storesData = Array.isArray(res.data) ? res.data : 
                        (res.data.stores || res.data.data || []);
      setStores(storesData);
      closeModal();
    } catch (err) {
      setError("Error saving store");
      console.error("Save store error:", err);
    }
  };

  return (
    <div className="bg-gray-100 mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">Store Maintenance</h1>

      {error && <div className="bg-red-200 text-red-800 p-3 mb-6 rounded">{error}</div>}

      <button 
        onClick={openAddModal} 
        className="mb-4 px-4 py-2 transition duration-300 bg-teal-500 text-white rounded hover:bg-teal-600"
      >
        + Add Store
      </button>

      {loading ? (
        <p>Loading stores...</p>
      ) : (
        <table className="min-w-full text-left bg-white border border-gray-200">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b">Store Name</th>
              <th className="py-2 px-4 border-b">Location</th>
              <th className="py-2 px-4 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Safely render stores array */}
            {Array.isArray(stores) && stores.length > 0 ? (
              stores.map((store) => (
                <tr key={store._id}>
                  <td className="py-2 px-4 border-b">{store.name}</td>
                  <td className="py-2 px-4 border-b">{store.location}</td>
                  <td className="py-2 px-4 border-b">
                    <button 
                      onClick={() => openEditModal(store)} 
                      className="bg-teal-500 text-white py-1 px-3 rounded mr-2"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => openConfirmModal(store)} 
                      className="bg-red-500 text-white py-1 px-3 rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="py-4 px-4 text-center text-gray-500">
                  No stores found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Confirmation Modal for Deleting Store */}
      <ConfirmModal 
        isOpen={confirmModalOpen} 
        message={`Delete store "${selectedStore?.name}"?`} 
        onConfirm={deleteStore} 
        onCancel={() => setConfirmModalOpen(false)} 
      />

      {/* Add/Edit Store Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h2 className="text-2xl font-bold mb-4">
              {selectedStore ? "Edit Store" : "Add Store"}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Store Name Input */}
              <label className="block mb-2">Store Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full p-2 border rounded mb-1 ${
                  submitted && !formData.name ? "border-red-500" : ""
                }`}
              />
              {submitted && !formData.name && (
                <p className="text-red-500 text-sm mb-2">Store Name is required</p>
              )}

              {/* Store Location Input */}
              <label className="block mb-2">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className={`w-full p-2 border rounded mb-1 ${
                  submitted && !formData.location ? "border-red-500" : ""
                }`}
              />
              {submitted && !formData.location && (
                <p className="text-red-500 text-sm mb-2">Location is required</p>
              )}

              {/* Buttons */}
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreMaintenance;